import { Analytics } from '@cobliteam/analytics';
import { type Vehicle, VehicleDriverAuthorizationMode } from '@cobliteam/common-types';
import useDriver from '@cobliteam/hooks/src/useEntity/useDriver';
import useVehicle from '@cobliteam/hooks/src/useEntity/useVehicle';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Input from '@cobliteam/ui/form/Input';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Check, Search } from 'lucide-react';
import { type KeyboardEvent, useMemo, useRef, useState } from 'react';

interface CreatedDriver {
  id: string;
  name: string;
}

interface DriverStepBodyProps {
  fleetId?: string;
  /** Called once the driver is created, so the guide can be persisted as dismissed. */
  onRegistered?: () => void;
  /** Called after the new driver is associated to a vehicle, to advance to the next step. */
  onCompleted?: () => void;
}

/** Height of a single vehicle row (px) — keeps the virtual window stable. */
const ROW_HEIGHT = 56;
/** Only ~5 rows are visible; the rest scroll within the virtualized window. */
const VISIBLE_ROWS = 5;
/** Linger after a successful association so the user sees the success state before advancing. */
const ADVANCE_DELAY_MS = 900;

const vehicleLabel = (vehicle: Vehicle) => vehicle.licensePlate || vehicle.label || '';

/** A single vehicle row that associates the freshly-created driver on click. */
const VehicleAssociateRow = ({ vehicle, driverId, fleetId, onAssociated }: { vehicle: Vehicle; driverId: string; fleetId?: string; onAssociated?: () => void }) => {
  const { t } = useTranslation('basic-value-onboarding');
  const { mutateAsync: updateAuthorizations, isPending, isSuccess } = useVehicle().useUpdateVehicleDriverAuthorizations;

  const handleAssociate = async () => {
    await updateAuthorizations({
      vehicleId: vehicle.id,
      driverIds: [driverId],
      isImmobilizerEnabled: false,
      vehicleDriverAuthorizationMode: VehicleDriverAuthorizationMode.AllowChosenDriver,
    });
    Analytics.track('BasicValueModalDriverAssociated', { fleetId, vehicleId: vehicle.id, driverId });
    onAssociated?.();
  };

  return (
    <div className='flex h-12 items-center justify-between gap-3 rounded-lg border border-gray-6 border-solid px-3'>
      <Typography variant='text-sm' className='min-w-0 shrink truncate font-medium'>
        {vehicleLabel(vehicle)}
      </Typography>
      {isSuccess ? (
        <Typography variant='text-sm' className='flex shrink-0 items-center gap-1 text-success'>
          <Check size={16} aria-hidden />
          {t('association.associate_success')}
        </Typography>
      ) : (
        <Button size='sm' variant='outlined' loading={isPending} onClick={handleAssociate}>
          {t('associate')}
        </Button>
      )}
    </div>
  );
};

/** Inline driver registration followed by an inline "associate to a vehicle" step. */
const DriverStepBody = ({ fleetId, onRegistered, onCompleted }: DriverStepBodyProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const { mutateAsync: saveDriver, isPending: isSaving } = useDriver().useSaveDriver();

  const [name, setName] = useState('');
  const [createdDriver, setCreatedDriver] = useState<CreatedDriver | null>(null);
  const [search, setSearch] = useState('');

  const handleAssociated = () => {
    // Show the success state briefly, then advance to the next step.
    window.setTimeout(() => onCompleted?.(), ADVANCE_DELAY_MS);
  };

  // Vehicles are only needed once we reach the association step.
  const { data: vehicles } = useVehicle().getVehicles({ enabled: createdDriver !== null });

  const filteredVehicles = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = vehicles ?? [];
    if (!term) return list;
    return list.filter((vehicle) => vehicleLabel(vehicle).toLowerCase().includes(term));
  }, [vehicles, search]);

  const scrollParentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredVehicles.length,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  const handleRegister = async () => {
    const trimmed = name.trim();
    if (!trimmed || isSaving) return;
    const driver = await saveDriver({ id: '', fleetId: fleetId ?? '', name: trimmed, phoneNumbers: [] });
    Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'drivers' });
    setCreatedDriver({ id: driver.id, name: driver.name });
    onRegistered?.();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleRegister();
    }
  };

  // Step 1 — register the driver inline.
  if (!createdDriver) {
    return (
      <div className='flex items-end gap-2'>
        <Input
          autoFocus
          autoComplete='off'
          data-1p-ignore
          className='w-full'
          placeholder={t('driver_name_placeholder')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button onClick={handleRegister} loading={isSaving} disabled={!name.trim()}>
          {t('features.drivers.cta')}
        </Button>
      </div>
    );
  }

  // Step 2 — associate the new driver to a vehicle.
  return (
    <div className='flex flex-col gap-3'>
      <Typography variant='text-md-medium'>{t('association.associate_to_vehicle_question', { name: createdDriver.name })}</Typography>
      <Input startIcon={<Search size={16} />} placeholder={t('association.search_placeholder')} value={search} onChange={(event) => setSearch(event.target.value)} />
      {filteredVehicles.length === 0 ? (
        <Typography variant='text-sm' className='py-4 text-center text-gray-11'>
          {t('association.no_vehicles_found')}
        </Typography>
      ) : (
        <div ref={scrollParentRef} className='overflow-y-auto' style={{ height: VISIBLE_ROWS * ROW_HEIGHT }}>
          <div className='relative w-full' style={{ height: rowVirtualizer.getTotalSize() }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const vehicle = filteredVehicles[virtualRow.index];
              return (
                <div key={vehicle.id} className='absolute top-0 left-0 w-full pb-2' style={{ height: ROW_HEIGHT, transform: `translateY(${virtualRow.start}px)` }}>
                  <VehicleAssociateRow vehicle={vehicle} driverId={createdDriver.id} fleetId={fleetId} onAssociated={handleAssociated} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverStepBody;
